import { useState, useEffect } from 'react';
import {
  BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart, PieChart, Pie, Cell,
} from 'recharts';
import {
  TrendingUp, TriangleAlert as AlertTriangle, DollarSign, Clock,
  Award, RefreshCw, ArrowRight, Settings2, Zap, Shield, Scale,
  Users, Server, Rocket,
} from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account } from '../../types';
import type {
  EAWorkspace, EAAnalyticsSnapshot, ValueDriverType, CurrencyCode,
} from '../../types/executiveAnalytics';
import { VALUE_DRIVER_LABELS } from '../../types/executiveAnalytics';
import { formatCurrencyValue } from '../../lib/executive-analytics/currency';
import { APP_CHART } from '../../lib/chartTheme';

interface AnalyticsTabProps {
  account: Account;
  workspace: EAWorkspace;
  snapshot: EAAnalyticsSnapshot | null;
  onWorkspaceUpdate: () => Promise<void>;
  onProceedToReport: () => void;
  onEditConfigure: () => void;
}

const fmtPct = (val: number | null | undefined): string => {
  if (val == null || Number.isNaN(val)) return 'N/A';
  return `${val.toFixed(1)}%`;
};

const scoreColor = (s: number) => s > 70 ? 'bg-app-positive' : s >= 40 ? 'bg-app-warning' : 'bg-app-critical';
const scoreTextColor = (s: number) => s > 70 ? 'text-app-positive' : s >= 40 ? 'text-app-warning' : 'text-app-critical';

const severityColor: Record<string, string> = {
  critical: 'bg-app-critical/10 text-app-critical',
  high: 'bg-app-warning/10 text-app-warning',
  medium: 'bg-app-warning/10 text-app-warning',
  low: 'bg-app-positive/10 text-app-positive',
};

const DRIVER_ICONS: Record<ValueDriverType, typeof TrendingUp> = {
  revenue_growth: TrendingUp,
  cost_efficiency: DollarSign,
  risk_reduction: Shield,
  compliance_regulatory: Scale,
  productivity_workforce: Users,
  infrastructure_performance: Server,
  transformation_strategic: Rocket,
};

const DRIVER_COLORS: Record<ValueDriverType, string> = {
  revenue_growth: APP_CHART.positive,
  cost_efficiency: APP_CHART.focus,
  risk_reduction: APP_CHART.warning,
  compliance_regulatory: APP_CHART.brand,
  productivity_workforce: APP_CHART.accent,
  infrastructure_performance: APP_CHART.critical,
  transformation_strategic: APP_CHART.muted,
};

function getBenefitCategoryBreakdown(fin: EAAnalyticsSnapshot['financial_outputs']): { driver: string; value: number; color: string }[] {
  const bd = fin.benefit_breakdown;
  if (!bd) {
    const items: { driver: string; value: number; color: string }[] = [];
    if (fin.revenue_impact > 0) items.push({ driver: 'Revenue Upside', value: fin.revenue_impact, color: DRIVER_COLORS.revenue_growth });
    const hard = (fin.cost_savings || 0) + (fin.labour_savings || 0);
    if (hard > 0) items.push({ driver: 'Hard Savings', value: hard, color: DRIVER_COLORS.cost_efficiency });
    if ((fin.workforce_productivity_savings || 0) > 0) items.push({ driver: 'Soft Savings', value: fin.workforce_productivity_savings, color: DRIVER_COLORS.productivity_workforce });
    const risk = (fin.risk_savings || 0) + (fin.compliance_savings || 0) + (fin.downtime_savings || 0);
    if (risk > 0) items.push({ driver: 'Risk Avoidance', value: risk, color: DRIVER_COLORS.risk_reduction });
    return items;
  }
  const items: { driver: string; value: number; color: string }[] = [];
  if (bd.hard_savings > 0) items.push({ driver: 'Hard Savings', value: bd.hard_savings, color: DRIVER_COLORS.cost_efficiency });
  if (bd.soft_savings > 0) items.push({ driver: 'Soft Savings', value: bd.soft_savings, color: DRIVER_COLORS.productivity_workforce });
  if (bd.revenue_upside > 0) items.push({ driver: 'Revenue Upside', value: bd.revenue_upside, color: DRIVER_COLORS.revenue_growth });
  if (bd.risk_avoidance > 0) items.push({ driver: 'Risk Avoidance', value: bd.risk_avoidance, color: DRIVER_COLORS.risk_reduction });
  return items;
}

export default function AnalyticsTab({
  account, workspace, snapshot, onWorkspaceUpdate, onProceedToReport, onEditConfigure,
}: AnalyticsTabProps) {
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (workspace.analytics_status === 'generating') {
      interval = setInterval(() => { onWorkspaceUpdate(); }, 5000);
    }
    return () => clearInterval(interval);
  }, [workspace.analytics_status, onWorkspaceUpdate]);

  const runAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiPost(`/v1/ea/workspace/${workspace.id}/run-analytics`, { workspace_id: workspace.id });
      await onWorkspaceUpdate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to run analytics');
    } finally {
      setLoading(false);
    }
  };

  if (workspace.configure_status !== 'validated') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Settings2 className="w-16 h-16 text-app-line-strong mx-auto mb-4" />
          <h3 className="text-lg font-medium text-app-ink mb-2">Configuration Required</h3>
          <p className="text-app-muted mb-6 max-w-sm mx-auto">Complete and validate your deal configuration before running analytics.</p>
          <button onClick={onEditConfigure} className="px-6 py-3 bg-app-brand text-white rounded-lg hover:bg-app-brand transition-colors">
            Go to Configure
          </button>
        </div>
      </div>
    );
  }

  if (workspace.analytics_status === 'none') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <TrendingUp className="w-16 h-16 text-app-brand mx-auto mb-4" />
          <h3 className="text-lg font-medium text-app-ink mb-2">Ready to Generate Analytics</h3>
          <p className="text-app-muted mb-6 max-w-sm mx-auto">Run the deterministic financial model and evidence engine against your configured deal data.</p>
          {error && <p className="text-sm text-app-critical mb-4">{error}</p>}
          <button onClick={runAnalytics} disabled={loading} className="px-6 py-3 bg-app-brand text-white rounded-lg hover:bg-app-brand disabled:opacity-50 transition-colors">
            {loading ? 'Starting...' : 'Run Analytics'}
          </button>
        </div>
      </div>
    );
  }

  if (workspace.analytics_status === 'generating') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-app-brand mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-app-ink mb-2">Generating Analytics...</h3>
          <p className="text-app-muted">This may take a minute. We'll update automatically when complete.</p>
        </div>
      </div>
    );
  }

  if (workspace.analytics_status === 'error') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-app-critical mx-auto mb-4" />
          <h3 className="text-lg font-medium text-app-ink mb-2">Analytics Generation Failed</h3>
          <p className="text-app-muted mb-6">{workspace.error_message || 'An unexpected error occurred. Please try again.'}</p>
          {error && <p className="text-sm text-app-critical mb-4">{error}</p>}
          <div className="flex items-center justify-center gap-3">
            <button onClick={onEditConfigure} className="px-5 py-2.5 border border-app-line-strong text-app-ink rounded-lg hover:bg-app-canvas transition-colors text-sm font-medium">
              Review Configuration
            </button>
            <button onClick={runAnalytics} disabled={loading} className="px-6 py-2.5 bg-app-brand text-white rounded-lg hover:bg-app-brand disabled:opacity-50 transition-colors text-sm font-medium">
              {loading ? 'Retrying...' : 'Retry Analytics'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return <div className="flex items-center justify-center h-96"><p className="text-app-muted">No analytics snapshot available.</p></div>;
  }

  const { financial_outputs: fin, scenario_outputs: sc, sensitivity_outputs: sens, scores, strategic_alignment: sa, risks } = snapshot;
  const activeDrivers = snapshot.active_value_drivers || [];
  const driverContext = workspace.value_driver_context;
  const currency = (workspace.currency || 'USD') as CurrencyCode;
  const fmtC = (val: number | null | undefined) => formatCurrencyValue(val ?? null, currency);
  const benefitBreakdown = getBenefitCategoryBreakdown(fin);

  const cashFlowData = fin.cash_flows.map(cf => ({
    year: `Year ${cf.year}`,
    gross_benefit: cf.gross_benefit,
    recurring_cost: -cf.recurring_cost,
    cumulative: cf.cumulative,
  }));

  return (
    <div className="space-y-8 pb-8">
      {activeDrivers.length > 0 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-3">Active Value Drivers</h3>
          {driverContext?.deal_narrative && (
            <p className="text-sm text-app-muted mb-4">{driverContext.deal_narrative}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {activeDrivers.map(dt => {
              const Icon = DRIVER_ICONS[dt] || Zap;
              return (
                <span
                  key={dt}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-app-brand/10 text-app-brand border border-app-brand/20"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {VALUE_DRIVER_LABELS[dt]}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={TrendingUp} label="Annualised ROI" value={fmtPct(fin.annualised_roi_percent ?? fin.roi_percent)} />
        <MetricCard icon={Clock} label="Payback Period" value={fin.payback_months != null ? `${fin.payback_months} mo` : 'N/A'} />
        <MetricCard icon={DollarSign} label="Annual Net Benefit" value={fmtC(fin.annual_net_benefit)} />
        <MetricCard icon={Award} label="Deal Quality" value={scores ? `${scores.deal_quality.toFixed(0)} / 100` : 'N/A'} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {fin.irr != null && <MetricCard icon={TrendingUp} label="IRR" value={fmtPct(fin.irr)} />}
        {fin.npv != null && <MetricCard icon={DollarSign} label="NPV" value={fmtC(fin.npv)} />}
        {fin.risk_adjusted_npv != null && <MetricCard icon={Shield} label="Risk-Adjusted NPV" value={fmtC(fin.risk_adjusted_npv)} />}
        {fin.bcr != null && <MetricCard icon={Scale} label="Benefit-to-Cost Ratio" value={`${fin.bcr.toFixed(2)}x`} />}
        {fin.tco > 0 && <MetricCard icon={DollarSign} label="Total Cost of Ownership" value={fmtC(fin.tco)} />}
      </div>

      {benefitBreakdown.length > 1 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Benefit Contribution by Driver</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={benefitBreakdown}
                    dataKey="value"
                    nameKey="driver"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(props: any) => `${props.name || ''} ${((props.percent || 0) * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {benefitBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtC(Number(v))} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {benefitBreakdown.sort((a, b) => b.value - a.value).map((item, i) => {
                const pct = fin.total_annual_gross_benefit > 0 ? (item.value / fin.total_annual_gross_benefit) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-app-ink truncate">{item.driver}</span>
                        <span className="text-sm font-semibold text-app-ink ml-2">{fmtC(item.value)}</span>
                      </div>
                      <div className="w-full bg-app-line rounded-full h-1.5" role="progressbar" aria-label={`${item.driver} share`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 border-t border-app-line flex items-center justify-between">
                <span className="text-sm font-semibold text-app-ink">Total Annual Gross Benefit</span>
                <span className="text-sm font-bold text-app-ink">{fmtC(fin.total_annual_gross_benefit)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {fin.benefit_breakdown && (fin.benefit_breakdown.ebitda_relevant > 0 || fin.ev_impact) && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">EBITDA & Enterprise Value Impact</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-app-line rounded-lg p-4">
              <div className="text-xs text-app-muted mb-1">EBITDA-Relevant Impact</div>
              <div className="text-xl font-bold text-app-ink">{fmtC(fin.benefit_breakdown.ebitda_relevant)}</div>
              <div className="text-[10px] text-app-muted mt-1">Hard savings + margin-adjusted revenue</div>
            </div>
            {fin.margin_improvement != null && (
              <div className="border border-app-line rounded-lg p-4">
                <div className="text-xs text-app-muted mb-1">Margin Improvement</div>
                <div className="text-xl font-bold text-app-ink">{fmtPct(fin.margin_improvement)}</div>
              </div>
            )}
            {fin.ev_impact != null && (
              <div className="border border-app-line rounded-lg p-4">
                <div className="text-xs text-app-muted mb-1">Enterprise Value Impact</div>
                <div className="text-xl font-bold text-app-ink">{fmtC(fin.ev_impact)}</div>
                <div className="text-[10px] text-app-muted mt-1">Modelled estimate</div>
              </div>
            )}
          </div>
        </div>
      )}

      {fin.guardrails && fin.guardrails.length > 0 && (
        <div className="bg-app-warning/10 border border-app-warning rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-warning mb-3">Model Guardrails</h3>
          <div className="space-y-2">
            {fin.guardrails.map((g, i) => (
              <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-lg ${
                g.severity === 'critical' ? 'bg-app-critical/10 border border-app-critical' : 'bg-app-warning/10 border border-app-warning'
              }`}>
                <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${g.severity === 'critical' ? 'text-app-critical' : 'text-app-warning'}`} />
                <div>
                  <span className={`text-xs font-semibold uppercase ${g.severity === 'critical' ? 'text-app-critical' : 'text-app-warning'}`}>{g.severity}</span>
                  <p className="text-sm text-app-ink mt-0.5">{g.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-app-surface border border-app-line rounded-lg p-6">
        <h3 className="text-lg font-semibold text-app-ink mb-4">Cash Flow Analysis</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={cashFlowData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis tickFormatter={v => fmtC(v as number)} />
            <Tooltip formatter={(v) => fmtC(Number(v))} />
            <Legend />
            <Bar dataKey="gross_benefit" fill={APP_CHART.positive} name="Gross benefit" />
            <Bar dataKey="recurring_cost" fill={APP_CHART.critical} name="Recurring cost" />
            <Line type="monotone" dataKey="cumulative" stroke={APP_CHART.brand} strokeWidth={2} name="Cumulative net" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {sc && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Scenario Comparison</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {(['downside', 'base', 'upside'] as const).map(key => {
              const d = sc[key];
              return (
                <div key={key} className={`border rounded-lg p-4 ${key === 'base' ? 'border-app-brand/20 bg-app-brand/10' : 'border-app-line'}`}>
                  <h4 className="font-medium text-app-ink mb-3 capitalize">{key}{key === 'base' && <span className="ml-2 text-xs text-app-brand font-normal">(Current)</span>}</h4>
                  <div className="space-y-2 text-sm">
                    <Row label="Annualised ROI" value={fmtPct(d.annualised_roi_percent ?? d.roi_percent)} />
                    {d.irr != null && <Row label="IRR" value={fmtPct(d.irr)} />}
                    <Row label="Net Benefit" value={fmtC(d.annual_net_benefit)} />
                    <Row label="Payback" value={d.payback_months != null ? `${d.payback_months} mo` : 'N/A'} />
                    <Row label="NPV" value={fmtC(d.npv)} />
                    <Row label="EBITDA Impact" value={fmtC(d.ebitda_impact)} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sens && sens.length > 0 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Sensitivity Analysis</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-app-line text-app-muted">
                <th className="text-left py-2 font-medium">Variable</th>
                <th className="text-right py-2 font-medium">Base</th>
                <th className="text-right py-2 font-medium">ROI (Low)</th>
                <th className="text-right py-2 font-medium">ROI (High)</th>
                <th className="text-right py-2 font-medium">Spread</th>
              </tr>
            </thead>
            <tbody>
              {[...sens].sort((a, b) => b.delta_roi - a.delta_roi).map((s, i) => {
                const spread = s.delta_roi;
                const bg = spread > 50 ? 'bg-app-warning' : spread > 20 ? 'bg-app-warning' : 'bg-app-warning';
                return (
                  <tr key={i} className="border-b border-app-line">
                    <td className="py-2 text-app-ink">{s.label}</td>
                    <td className="py-2 text-app-muted text-right">{s.base_value.toLocaleString()}</td>
                    <td className="py-2 text-app-muted text-right">{fmtPct(s.roi_at_low)}</td>
                    <td className="py-2 text-app-muted text-right">{fmtPct(s.roi_at_high)}</td>
                    <td className="py-2 text-right">
                      <span className={`inline-block px-2 py-0.5 rounded text-white text-xs font-medium ${bg}`}>{fmtPct(spread)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {scores && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Deal Quality Scores</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(scores).map(([key, val]) => (
              <div key={key} className="border border-app-line rounded-lg p-4">
                <div className="text-xs text-app-muted mb-2 capitalize">{key.replace(/_/g, ' ')}</div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-app-line rounded-full h-2" role="progressbar" aria-label={`${key.replace(/_/g, ' ')} score`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(val, 100)}>
                    <div className={`${scoreColor(val)} h-2 rounded-full`} style={{ width: `${Math.min(val, 100)}%` }} />
                  </div>
                  <span className={`text-sm font-bold ${scoreTextColor(val)}`}>{val.toFixed(0)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sa && sa.items && sa.items.length > 0 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Strategic Alignment</h3>
          <div className="space-y-3">
            {sa.items.map((item, i) => (
              <div key={i} className="border border-app-line rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="font-medium text-app-ink mb-1">{item.objective}</div>
                    <div className="text-sm text-app-muted">{item.evidence}</div>
                  </div>
                  <span className="ml-4 px-2 py-0.5 text-xs font-medium rounded bg-app-brand/10 text-app-brand">{item.confidence}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-app-line rounded-full h-2" role="progressbar" aria-label={`${item.objective} alignment`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={item.alignment_score}>
                    <div className={`${scoreColor(item.alignment_score)} h-2 rounded-full`} style={{ width: `${item.alignment_score}%` }} />
                  </div>
                  <span className="text-sm font-medium text-app-ink">{item.alignment_score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {snapshot.value_driver_tree && snapshot.value_driver_tree.length > 0 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Value Driver Map</h3>
          <div className="space-y-3">
            {snapshot.value_driver_tree.map((node, i) => (
              <div key={i} className="border border-app-line rounded-lg p-4">
                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <div className="text-xs text-app-muted mb-1">Capability</div>
                    <div className="font-medium text-app-ink">{node.capability}</div>
                  </div>
                  <div>
                    <div className="text-xs text-app-muted mb-1">Operational Change</div>
                    <div className="text-app-ink">{node.operational_change}</div>
                  </div>
                  <div>
                    <div className="text-xs text-app-muted mb-1">KPI</div>
                    <div className="text-app-ink">{node.kpi}</div>
                  </div>
                  <div>
                    <div className="text-xs text-app-muted mb-1">Financial Impact</div>
                    <div className="font-medium text-app-brand">
                      {node.estimated_value ? fmtC(node.estimated_value) : node.financial_impact}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {risks && risks.length > 0 && (
        <div className="bg-app-surface border border-app-line rounded-lg p-6">
          <h3 className="text-lg font-semibold text-app-ink mb-4">Risk Assessment</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-app-line text-app-muted">
                <th className="text-left py-2 font-medium">Risk</th>
                <th className="text-center py-2 font-medium">Severity</th>
                <th className="text-center py-2 font-medium">Likelihood</th>
                <th className="text-left py-2 font-medium">Mitigation</th>
              </tr>
            </thead>
            <tbody>
              {risks.map((r, i) => (
                <tr key={i} className="border-b border-app-line">
                  <td className="py-3 text-app-ink">{r.description}</td>
                  <td className="py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${severityColor[r.severity] || 'bg-app-canvas text-app-ink'}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="py-3 text-app-muted text-center capitalize">{r.likelihood.replace(/_/g, ' ')}</td>
                  <td className="py-3 text-app-muted">{r.mitigation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between bg-app-brand/10 border border-app-brand/20 rounded-lg p-6">
        <div>
          <h3 className="text-lg font-semibold text-app-ink mb-1">Analytics Complete</h3>
          <p className="text-sm text-app-muted">Review the analysis above, then proceed to generate your executive report.</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={onEditConfigure} className="flex items-center gap-1.5 text-app-brand hover:text-app-brand text-sm font-medium transition-colors">
            <Settings2 className="w-4 h-4" /> Edit Configuration
          </button>
          <button
            onClick={runAnalytics}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-app-brand/20 text-app-brand rounded-lg hover:bg-app-brand/10 disabled:opacity-50 transition-colors text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Regenerating...' : 'Regenerate Analytics'}
          </button>
          <button onClick={onProceedToReport} className="flex items-center gap-2 px-6 py-3 bg-app-brand text-white rounded-lg hover:bg-app-brand transition-colors font-medium">
            Proceed to Report <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value }: { icon: typeof TrendingUp; label: string; value: string }) {
  return (
    <div className="bg-app-surface border border-app-line rounded-lg p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-app-muted">{label}</span>
        <Icon className="w-4 h-4 text-app-brand" />
      </div>
      <div className="text-2xl font-bold text-app-ink">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-app-muted">{label}</span>
      <span className="font-medium text-app-ink">{value}</span>
    </div>
  );
}
